/**
 * Lexique métier partagé — même capacité module, libellé adapté au produit (Hospitol, Edu…).
 * Les modules sources (RH, inventory, billing) gardent leurs actions ; le shell adapte l'affichage.
 */

export type ProductContext = "hospitol" | "edu" | "mineportal";

export type LexiconKey =
  | "section.rh"
  | "section.supply"
  | "section.billing"
  | "section.integrations"
  | "rh.employees"
  | "rh.departments"
  | "rh.schedules"
  | "supply.articles"
  | "supply.levels"
  | "supply.receipts"
  | "supply.articlesAdmin"
  | "billing.invoices"
  | "billing.revenue"
  | "inventory.article"
  | "inventory.articles"
  | "stock.onHand"
  | "stock.reserved"
  | "stock.available"
  | "stock.threshold"
  | "stock.lowAlert"
  | "action.advancedModule"
  | "action.printLabel"
  | "action.labelAndStockIn";

type LexiconTable = Record<LexiconKey, string>;

const HOSPITOL_LEXICON: LexiconTable = {
  "section.rh": "Personnel hospitalier",
  "section.supply": "Approvisionnement",
  "section.billing": "Facturation",
  "section.integrations": "Intégrations",
  "rh.employees": "Personnel soignant",
  "rh.departments": "Services & unités",
  "rh.schedules": "Plannings de garde",
  "supply.articles": "Consommables & médicaments",
  "supply.levels": "Niveaux de stock",
  "supply.receipts": "Réceptions pharmacie",
  "supply.articlesAdmin": "Catalogue ISC (admin)",
  "billing.invoices": "Recettes & dispensation",
  "billing.revenue": "Recettes consolidées",
  "inventory.article": "Article hospitalier",
  "inventory.articles": "Articles hospitaliers",
  "stock.onHand": "En stock",
  "stock.reserved": "Réservé (factures)",
  "stock.available": "Disponible",
  "stock.threshold": "Seuil minimum",
  "stock.lowAlert": "Sous seuil",
  "action.advancedModule": "Console avancée du module",
  "action.printLabel": "Imprimer l'étiquette",
  "action.labelAndStockIn": "Étiqueter + entrée en stock",
};

const EDU_LEXICON: LexiconTable = {
  "section.rh": "Équipe éducative",
  "section.supply": "Stock établissement",
  "section.billing": "Finance scolaire",
  "section.integrations": "Modules connectés",
  "rh.employees": "Équipe pédagogique",
  "rh.departments": "Services de l'établissement",
  "rh.schedules": "Plannings équipe",
  "supply.articles": "Stock & cantine",
  "supply.levels": "Niveaux de stock",
  "supply.receipts": "Entrées de stock",
  "supply.articlesAdmin": "Catalogue stock (admin)",
  "billing.invoices": "Facturation scolaire",
  "billing.revenue": "Recettes scolarité",
  "inventory.article": "Article scolaire",
  "inventory.articles": "Articles scolaires",
  "stock.onHand": "Quantité en stock",
  "stock.reserved": "Réservé",
  "stock.available": "Disponible",
  "stock.threshold": "Seuil d'alerte",
  "stock.lowAlert": "Stock bas",
  "action.advancedModule": "Administration module source",
  "action.printLabel": "Imprimer l'étiquette",
  "action.labelAndStockIn": "Étiqueter + stocker",
};

const MINEPORTAL_LEXICON: LexiconTable = {
  "section.rh": "Personnel & mobilisation",
  "section.supply": "Approvisionnement MRO",
  "section.billing": "Facturation & imputation",
  "section.integrations": "Intégrations",
  "rh.employees": "Effectifs",
  "rh.departments": "Services site",
  "rh.schedules": "Plannings & rotations",
  "supply.articles": "Pièces & consommables",
  "supply.levels": "Niveaux de stock",
  "supply.receipts": "Réceptions magasin",
  "supply.articlesAdmin": "Catalogue ISC (admin)",
  "billing.invoices": "Factures & charges",
  "billing.revenue": "Imputation coûts",
  "inventory.article": "Article MRO",
  "inventory.articles": "Articles MRO",
  "stock.onHand": "En stock",
  "stock.reserved": "Réservé (OT / ISRM)",
  "stock.available": "Disponible",
  "stock.threshold": "Seuil minimum",
  "stock.lowAlert": "Sous seuil",
  "action.advancedModule": "Console avancée du module",
  "action.printLabel": "Imprimer l'étiquette",
  "action.labelAndStockIn": "Étiqueter + entrée en stock",
};

const LEXICONS: Record<ProductContext, LexiconTable> = {
  hospitol: HOSPITOL_LEXICON,
  edu: EDU_LEXICON,
  mineportal: MINEPORTAL_LEXICON,
};

export function resolveLexicon(context: ProductContext, key: LexiconKey): string {
  return LEXICONS[context][key] ?? key;
}

/** Alias court pour les menus produit. */
export function createLexicon(context: ProductContext) {
  return (key: LexiconKey) => resolveLexicon(context, key);
}
